import { useCallback, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import { showingDockedCopilotState, useCurrentOrganization } from 'app/AppState';
import { ErrorBoundary } from 'components/error-boundary';
import CopilotRoot from 'features/copilot/CopilotRoot';
import { Organization } from 'interfaces';
import { styled } from 'stitches';

const CopilotDockRoot = styled('div', {
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  transform: 'translateX(100%)',
  minWidth: '500px',
  maxWidth: '50%',
  position: 'absolute',
  top: 0,
  right: 0,
  height: '100%',
  overflow: 'hidden',
  backgroundColor: 'white',
  transition: 'transform 0.3s ease-in-out',
  zIndex: '$3',

  variants: {
    show: {
      true: {
        transform: 'translateX(0)',
      },
    },
  },
});

const CopilotDockContainer = styled('div', {
  width: '100%',
  height: '100%',
});

const Resizer = styled('div', {
  width: '10px',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  cursor: 'ew-resize',
  zIndex: '$4',
});

type CopilotDockProps = {
  show: boolean;
};

export function CopilotDock(props: CopilotDockProps) {
  const { show } = props;
  const setShowDockedCopilot = useSetRecoilState(showingDockedCopilotState);
  const currentOrganization = useCurrentOrganization(false) as Organization | null;
  const [width, setWidth] = useState(400);

  const onCopilotDimissed = useCallback(() => {
    setShowDockedCopilot(false);
  }, [setShowDockedCopilot]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const startX = e.clientX;
    const startWidth = width;

    const onMouseMove = (e: MouseEvent) => {
      requestAnimationFrame(() => {
        const newWidth = startWidth - (e.clientX - startX);
        setWidth(Math.max(newWidth, 300));
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = 'auto';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    // Prevent text selection while resizing.
    document.body.style.userSelect = 'none';
  };

  const memoizedCopilotDockContainer = useMemo(() => {
    return (
      <CopilotDockContainer>
        <ErrorBoundary>
          <CopilotRoot organizationId={currentOrganization?.id} onDismiss={onCopilotDimissed} />
        </ErrorBoundary>
      </CopilotDockContainer>
    );
  }, [currentOrganization?.id, onCopilotDimissed]);

  return (
    <CopilotDockRoot show={show} css={{ width }}>
      <Resizer onMouseDown={handleMouseDown} />
      {memoizedCopilotDockContainer}
    </CopilotDockRoot>
  );
}
